import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Chart from 'components/chart';
import HeatmapMonthAxis from 'components/heatmap-month-axis';
import HeatmapRows from 'components/heatmap-rows';
import HeatmapTooltip from 'components/heatmap-tooltip';
import InteractionSurface from 'components/interaction-surface';
import Legend from 'components/legend';
import TimeAxis from 'components/time-axis';
import {VERTICAL} from 'constants/direction';
import {TOP} from 'constants/orient';
import heatmapDomain from 'domains/heatmap-domain';
import colorRange from 'ranges/color-range';
import linearScale from 'scales/linear-scale';
import {getTranslate} from 'utils/attribute-util';
import {getBins, getExtentsFromBins} from 'utils/data-util';
import {getChartWidth, getContentWidth, getGutters} from 'utils/layout-util';
import {isValid} from 'validators/number-validator';
import styles from './index.css';

import {getColorForValue, isVal} from 'common/components/range-color-picker';
import Colors from 'common/constants/colors';
import {
  formatNumber,
  formatNumberWithUnits,
} from 'common/formatters/number-formatter';
import {formatDate} from 'common/formatters/date-formatter';
import {DateTimeFormat} from 'common/proxies/enum-proxy';
import {arrayOfTypes} from 'common/validators';

export default class Heatmap extends React.Component {
  static propTypes = {
    colors: PropTypes.arrayOf(PropTypes.string),
    customColorRanges: PropTypes.arrayOf(
      arrayOfTypes([
        PropTypes.string,
        PropTypes.arrayOf(
          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        ),
      ]),
    ),
    groups: PropTypes.array,
    gutters: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.objectOf(PropTypes.number),
    ]),
    hasTooltip: PropTypes.bool,
    height: PropTypes.number,
    noDataMessage: PropTypes.string,
    rowHeight: PropTypes.number,
    width: PropTypes.number,
  };

  static defaultProps = {
    colors: [
      '#075100', // Dark green
      '#0b8500', // Green
      '#bedf3f', // Light green
      '#fff300', // Yellow
      '#ffc300', // Orange
      '#ff0000', // Red
    ],
    groups: [
      {
        series: [
          {
            data: [],
          },
        ],
      },
    ],
    gutters: 0,
    hasTooltip: true,
    height: 0,
    noDataMessage: '',
    rowHeight: 6,
    width: 0,
  };

  getChartHeight(height, rowHeight, days, gutters, legendRows) {
    // If height is explicit, return explicit height:
    if (isValid(height) && height > 0) {
      return Math.ceil(height);
    }
    // If height is not explicit, calculate from number of rows:
    return rowHeight * days.length + gutters.top * legendRows + gutters.top;
  }

  getColumnWidth(width) {
    const columnWidth = width / 24;
    return columnWidth > 0 ? columnWidth : 0;
  }

  getContentY(gutters, legendRows = 1) {
    return gutters.top * legendRows;
  }

  getDays(months) {
    return (
      months
        // Pull all days into a single array:
        .map((month) => month.days)
        // Flatten days array:
        .reduce((previous, current) => previous.concat(current))
    );
  }

  getScaleColor(hourValue, nullColor, scale) {
    if (hourValue === null) {
      return nullColor;
    }
    return scale(hourValue);
  }

  getCustomColor(hourValue, nullColor, customColorRanges) {
    if (hourValue === null) {
      return nullColor;
    }
    return getColorForValue(hourValue, customColorRanges, nullColor);
  }

  getHeatmapData(months, days, scale, nullColor, customColorRanges) {
    const dayTimestamp = moment(months[0].timestamp).subtract(1, 'days');
    return days.map((day) => {
      dayTimestamp.add(1, 'days');
      const hourTimestamp = dayTimestamp.clone().subtract(1, 'hours');
      return day.map((hourValue) => ({
        color: customColorRanges
          ? this.getCustomColor(hourValue, nullColor, customColorRanges)
          : this.getScaleColor(hourValue, nullColor, scale),
        timestamp: hourTimestamp.add(1, 'hours').toDate(),
        value: hourValue,
      }));
    });
  }

  getHeatmapGutters(gutters) {
    const box = getGutters(gutters);
    if (box.top > 0) {
      // If the top gutter is greater than zero,
      // the legend should be visible. Mandate
      // a height of 28px for the legend.
      box.top = 28;
    }
    return box;
  }

  getHours(days) {
    return days.reduce((previous, current) => previous.concat(current));
  }

  getLegendExtents(extents) {
    return extents.map((extent) =>
      extent.map((value) =>
        // For visual tidiness, round numbers larger than 1:
        Math.abs(value) >= 1 ? Math.round(value) : value,
      ),
    );
  }

  getLegendLabel(groups) {
    // The groups/series/data hierarchy keeps heatmap
    // config consistent with the other charts, at the
    // expense of this deep one dig to pull stuff out.
    if (
      groups &&
      groups.length &&
      groups[0].series &&
      groups[0].series.length &&
      groups[0].series[0].legendLabel
    ) {
      return groups[0].series[0].legendLabel;
    }
    return '';
  }

  getLegendRanges(legendExtents) {
    // The binning histogram creates ranges with the precise
    // values in each bin. Looking at the bins side by side,
    // they often appear to be discontinuous, because the value
    // at the top of one bin can be lower than the first value
    // in the next one up. Despite accurately representing the
    // underlying data, this makes users complain, because most
    // want to see a smooth range of values across the bins.
    // This function smoothes bins so that they appear to flow
    // into each other.
    return legendExtents.map((extent, index, arr) => {
      const next = arr[index + 1];
      return next ? [extent[0], next[0]] : extent;
    });
  }

  getLegendSeries(
    legendRanges,
    colorsForScale,
    legendLabel,
    numberFormattingFunc,
    clampToZero,
    noDataMessage,
  ) {
    const baseItems = [
      // The heatmap legend always contains an item
      // for "No data" at the beginning.
      {
        legendLabel: noDataMessage,
        style: {
          fill: Colors.white,
          stroke: Colors.gray500,
        },
      },

      // Only add a legend item for zero if clampToZero is true.
      // For some meter types, such as those which track temperature,
      // it doesn't make sense to clamp the range of values to zero.
      clampToZero
        ? {
            legendLabel: `0 ${legendLabel}`,
            style: {
              fill: colorsForScale[0],
            },
          }
        : null,
    ].filter((item) => item);

    const rangeItems = legendRanges
      .map((range, index) => {
        const start = numberFormattingFunc(range[0]);
        const end = numberFormattingFunc(range[1]);
        const dash = start && end ? '-' : '';
        const label = `${start}${dash}${end} ${legendLabel}`;
        const fill = colorsForScale[index + 1];
        return start || end
          ? {
              legendLabel: label,
              style: {
                fill,
              },
            }
          : null;
      })
      .filter((item) => item);
    return [...baseItems, ...rangeItems];
  }

  getLegendSeriesForCustomColorRanges(customColorRanges, legendLabel) {
    const colorsForScale = customColorRanges.map((item) => item[0]);
    const legendRanges = customColorRanges.map((item) => item[1]);
    return legendRanges
      .map(([start, end], index) => {
        let label;
        if (isVal(start) && isVal(end)) {
          label = `${start}-${end} ${legendLabel}`;
        } else if (isVal(end)) {
          label = `< ${end} ${legendLabel}`;
        } else if (isVal(start)) {
          label = `>= ${start} ${legendLabel}`;
        }
        const fill = colorsForScale[index];
        return start || end
          ? {
              legendLabel: label,
              style: {
                fill,
              },
            }
          : null;
      })
      .filter((item) => item);
  }

  getData(groups) {
    // The groups/series/data hierarchy keeps heatmap
    // config consistent with the other charts, at the
    // expense of this deep dig to pull the data out.
    if (
      groups &&
      groups.length &&
      groups[0].series &&
      groups[0].series.length &&
      groups[0].series[0].data
    ) {
      return groups[0].series[0].data;
    }
    return {};
  }

  getTimeAxisData(startTimestamp) {
    const date = moment(startTimestamp);
    return Array.from({length: 24}, () => {
      // moment.format() returns an ISO date string.
      // This is not for display and can therefore
      // go straight through moment. No need for formatDate().
      const isoDate = date.format();
      date.add(1, 'hour');
      return {
        date: isoDate,
        tickFormat: DateTimeFormat.TIME,
      };
    });
  }

  getTimeAxisDomain(timeAxisData) {
    const dates = timeAxisData.map((datum) => datum.date);
    return [moment(dates.shift()), moment(dates.pop())];
  }

  getTooltipProps(
    rowHeight,
    columnWidth,
    activeIndex,
    x,
    heatmapData,
    legendLabel,
    noDataMessage,
  ) {
    const columnIndex = Math.floor(x / columnWidth);
    if (isValid(columnIndex) && columnIndex >= 0) {
      const day = heatmapData[activeIndex];
      const hour = day && day[columnIndex];
      return hour
        ? {
            color: hour.color,
            description: formatDate(
              hour.timestamp,
              DateTimeFormat.TEXTUAL_DAY_OF_WEEK,
            ),
            title:
              hour.value === null
                ? noDataMessage
                : formatNumberWithUnits(hour.value, legendLabel),
            value: hour.value,
            x: columnWidth * columnIndex + columnWidth / 2,
            y: rowHeight * activeIndex + rowHeight / 2,
          }
        : null;
    }
    return null;
  }

  getTooltipX(columnWidth, x, gutters) {
    const columnIndex = Math.floor(x / columnWidth);
    return columnWidth * columnIndex + columnWidth / 2 + gutters.left;
  }

  getTooltipY(rowHeight, activeIndex, contentY, gutters) {
    return rowHeight * activeIndex + rowHeight / 2 + contentY + gutters.top;
  }

  shouldClampToZero(groups) {
    if (groups && groups.length && groups[0].clampToZero) {
      return true;
    }
    return false;
  }

  render() {
    const {
      colors,
      customColorRanges,
      groups,
      gutters,
      hasTooltip,
      height,
      noDataMessage,
      rowHeight,
      width,
    } = this.props;
    return (
      <Chart
        render={(chartProps) => {
          const {
            activeIndex,
            handleMove,
            handleOut,
            legendRows,
            setLegendRows,
            showTooltip,
            x,
          } = chartProps;

          const chartWidth = getChartWidth(width, this._node);
          const heatmapGutters = this.getHeatmapGutters(gutters);
          const clampToZero = this.shouldClampToZero(groups);
          const contentWidth = getContentWidth(
            chartWidth,
            heatmapGutters.left,
            0,
          );
          const contentY = this.getContentY(heatmapGutters, legendRows);
          const {results: months, startTimestamp} = this.getData(groups);
          const days = this.getDays(months);
          const hours = this.getHours(days);
          const chartHeight = this.getChartHeight(
            height,
            rowHeight,
            days,
            heatmapGutters,
            legendRows,
          );
          const columnWidth = this.getColumnWidth(contentWidth);
          const domain = heatmapDomain(hours, colors);
          const colorsForScale = colorRange(colors);
          const scale = linearScale(domain, colorsForScale);
          const heatmapData = this.getHeatmapData(
            months,
            days,
            scale,
            Colors.white,
            customColorRanges,
          );
          const timeAxisData = this.getTimeAxisData(startTimestamp);
          const timeAxisDomain = this.getTimeAxisDomain(timeAxisData);
          const bins = getBins(hours);
          const extents = getExtentsFromBins(bins);
          const legendLabel = this.getLegendLabel(groups);
          const legendExtents = this.getLegendExtents(extents);
          const legendRanges = this.getLegendRanges(legendExtents);
          const legendSeries = customColorRanges
            ? this.getLegendSeriesForCustomColorRanges(
                customColorRanges,
                legendLabel,
              )
            : this.getLegendSeries(
                legendRanges,
                colorsForScale,
                legendLabel,
                formatNumber,
                clampToZero,
                noDataMessage,
              );
          const tooltipX = this.getTooltipX(columnWidth, x, heatmapGutters);
          const tooltipY = this.getTooltipY(
            rowHeight,
            activeIndex,
            contentY,
            heatmapGutters,
          );
          const tooltipProps = this.getTooltipProps(
            rowHeight,
            columnWidth,
            activeIndex,
            x,
            heatmapData,
            legendLabel,
            noDataMessage,
          );
          return (
            <svg
              className={styles.Heatmap}
              height={chartHeight}
              ref={(node) => (this._node = node)}
              width={chartWidth}>
              <Legend
                height={heatmapGutters.top}
                series={legendSeries}
                setLegendRows={setLegendRows}
                width={chartWidth}
              />

              <g
                className={styles.HeatmapGroups}
                transform={getTranslate(0, contentY)}>
                <TimeAxis
                  data={timeAxisData}
                  domain={timeAxisDomain}
                  height={heatmapGutters.top}
                  numTicks={24}
                  orient={TOP}
                  tickPadding={0}
                  tickWidth={columnWidth}
                  width={contentWidth}
                  x={heatmapGutters.left}
                />

                <HeatmapMonthAxis
                  gutters={heatmapGutters}
                  months={months}
                  rowHeight={rowHeight}
                  y={heatmapGutters.top}
                />

                <HeatmapRows
                  columnWidth={columnWidth}
                  contentWidth={contentWidth}
                  height={chartHeight - heatmapGutters.top - contentY}
                  rowHeight={rowHeight}
                  rows={heatmapData}
                  x={heatmapGutters.left}
                  y={heatmapGutters.top}
                />
              </g>

              <InteractionSurface
                direction={VERTICAL}
                handleMove={handleMove}
                handleOut={handleOut}
                height={Math.ceil(heatmapData.length * rowHeight) - 1}
                tickRange={rowHeight}
                width={chartWidth - heatmapGutters.left - 1}
                x={heatmapGutters.left}
                y={contentY + heatmapGutters.top}
              />

              {hasTooltip && showTooltip && tooltipProps ? (
                <HeatmapTooltip
                  color={tooltipProps.color}
                  description={tooltipProps.description}
                  title={tooltipProps.title}
                  x={tooltipX}
                  y={tooltipY}
                />
              ) : null}
            </svg>
          );
        }}
      />
    );
  }
}
